function order(variants, reference, attributes) {
    function orderRec(left, right, attributeIndex) {
        if (attributeIndex >= attributes.length) {
            return;
        }
        const attribute = attributes[attributeIndex];
        const referenceValue = reference[attribute];
        const matched = [];
        const rest = [];
        for (let i = left; i < right; i++) {
            const variant = variants[i];
            (variant[attribute] === referenceValue ? matched : rest).push(variant);
        }
        for (let i = 0; i < matched.length; i++) {
            variants[left + i] = matched[i];
        }
        for (let i = 0; i < rest.length; i++) {
            variants[left + matched.length + i] = rest[i];
        }
        const insertIndex = left + matched.length;
        orderRec(left, insertIndex, attributeIndex + 1);
        orderRec(insertIndex, right, attributeIndex + 1);
    }
    orderRec(0, variants.length, 0);
}
function buildClusters(rules) {
    let clusters = [];
    for (const components of rules) {
        const [matchingClusters, rest] = match(clusters, (cluster) => components.some((component) => cluster.has(component)));
        if (matchingClusters.length) {
            const newCluster = new Set();
            for (const cluster of matchingClusters) {
                for (const component of cluster) {
                    newCluster.add(component);
                }
            }
            for (const component of components) {
                newCluster.add(component);
            }
            rest.push(newCluster);
            clusters = rest;
        }
        else {
            clusters.push(new Set(components));
        }
    }
    return clusters;
}
function match(xs, predicate) {
    const matched = [];
    const rest = [];
    for (const item of xs) {
        if (predicate(item)) {
            matched.push(item);
        }
        else {
            rest.push(item);
        }
    }
    return [matched, rest];
}

const EMPTY_EXPLAIN = {
    operation: '',
    componentKey: '',
    attributeKey: '',
    totalTime: 0,
    totalTraversals: 0,
    optimizations: [],
    totalPossibleCombinations: 0,
    totalCombinationsToTraverse: 0,
    variantsByComponents: {},
    clusters: [],
    componentsToTraverse: [],
    earlyExitBranches: 0,
    deadBranchEliminations: 0,
    perComponentCalls: {},
};
class LogicEngine {
    constructor(schema, rules, variantsByComponentKey) {
        this.schema = schema;
        this.rules = rules;
        this.variantsByComponentKey = variantsByComponentKey;
        this.componentKey = '';
        this.attributeKey = '';
        this.product = {};
        this.fixedAttributesByComponent = new Map();
        this.variationAttributesByComponentKey = new Map();
        this.explain = Object.assign({}, EMPTY_EXPLAIN);
        this.optimizations = {
            clustering: true,
            deadBranchElimination: true,
            earlyExit: true,
        };
        this.clusters = [];
        this.startedAt = 0;
        this.timeout = Infinity;
        this.filteredVariantsByComponentKey = new Map();
        this.componentKeys = [];
    }
    getAllValues(componentKey, attributeKey) {
        const variants = this.variantsByComponentKey.get(componentKey);
        if (!variants) {
            throw new Error(`Invalid component ${componentKey}`);
        }
        const possibleValues = new Set();
        for (const variant of variants) {
            possibleValues.add(variant[attributeKey]);
        }
        return Array.from(possibleValues);
    }
    isValid(product, validationOptions) {
        const result = this.validate(product, validationOptions);
        return result.isValid;
    }
    validate(product, validationOptions) {
        const result = {
            isValid: true,
            invalidRules: [],
            invalidComponentByVariants: [],
        };
        if (!validationOptions.ignoreAllRules) {
            result.invalidRules = this.rules.filter((rule) => !rule.fn(product)).map((rule) => rule.name);
        }
        const componentKeys = Object.keys(product);
        for (const componentKey of componentKeys) {
            const variants = this.variantsByComponentKey.get(componentKey);
            if (!variants) {
                throw new Error(`Cannot find variants for component "${componentKey}"`);
            }
            const variant = product[componentKey];
            if (!variant) {
                continue;
            }
            const attributes = this.schema.attributes
                .filter((attr) => attr.componentKey === componentKey)
                .map((it) => it.attributeKey);
            if (variant['_available'] === false) {
                if (variants.some((v) => v === null)) {
                    result.invalidComponentByVariants.push(componentKey);
                }
            }
            else if (!variants.some((v) => {
                for (const attr of attributes) {
                    if (attr !== '_available' && v[attr] !== variant[attr]) {
                        if (attr.startsWith('_') ||
                            !((v[attr] === null || v[attr] === undefined) &&
                                (variant[attr] === null || variant[attr] === undefined)))
                            return false;
                    }
                }
                return true;
            })) {
                result.invalidComponentByVariants.push(componentKey);
            }
        }
        result.isValid = result.invalidRules.length === 0 && result.invalidComponentByVariants.length === 0;
        return result;
    }
    getFirstCompatibleVariantIgnoringRules() {
        const result = {};
        for (const [componentKey, componentVariants] of this.variantsByComponentKey.entries()) {
            result[componentKey] = componentVariants[0];
        }
        return result;
    }
    getCompatibleValues(product, componentKey, attributeKey, validationOptions) {
        const values = this._getValues(product, componentKey, attributeKey, validationOptions);
        const filteredValues = values
            .filter((value) => value !== undefined);
        return filteredValues;
    }
    _getValues(product, componentKey, attributeKey, validationOptions) {
        this.resetExplain();
        this.explain.operation = 'getValues';
        this.initRecursion(product, componentKey, attributeKey);
        this.buildCombinationsInfo();
        this.buildPerComponentCalls();
        this.logEnabledOptimizations();
        const result = new Set();
        this.startedAt = Date.now();
        this.buildFilteredVariants(false);
        this.getValuesRec(0, product, new Map(), result, validationOptions);
        this.explain.totalTime = Date.now() - this.startedAt;
        const originalVariants = this.variantsByComponentKey.get(componentKey);
        if (originalVariants) {
            const orderIndex = new Map();
            for (const variant of originalVariants) {
                const val = variant[attributeKey];
                if (!orderIndex.has(val)) {
                    orderIndex.set(val, orderIndex.size);
                }
            }
            return Array.from(result).sort((a, b) => { var _a, _b; return ((_a = orderIndex.get(a)) !== null && _a !== void 0 ? _a : 0) - ((_b = orderIndex.get(b)) !== null && _b !== void 0 ? _b : 0); });
        }
        return Array.from(result);
    }
    logEnabledOptimizations() {
        this.explain.optimizations = Object.entries(this.optimizations)
            .filter(([_name, enabled]) => enabled)
            .map(([name]) => name);
    }
    buildCombinationsInfo() {
        this.explain.totalPossibleCombinations = 1;
        this.explain.totalCombinationsToTraverse = 1;
        const componentKeysSet = new Set(this.componentKeys);
        for (const [componentKey, variants] of this.variantsByComponentKey) {
            this.explain.totalPossibleCombinations *= variants.length;
            this.explain.variantsByComponents[componentKey] = variants.length;
            if (componentKeysSet.has(componentKey)) {
                this.explain.totalCombinationsToTraverse *= variants.length;
            }
        }
    }
    initRecursion(product, componentKey, attributeKey) {
        var _a;
        this.product = product;
        this.componentKey = componentKey;
        this.attributeKey = attributeKey;
        this.explain.componentKey = componentKey;
        this.explain.attributeKey = attributeKey;
        let componentKeys = Object.keys(product);
        if (this.optimizations.clustering) {
            this.buildClusters(componentKeys);
            this.buildClustersInfo();
            componentKeys = this.findClusterComponents(componentKey);
        }
        this.explain.componentsToTraverse = componentKeys;
        this.fixedAttributesByComponent = new Map();
        for (const componentKey of componentKeys) {
            this.variationAttributesByComponentKey.set(componentKey, []);
            this.fixedAttributesByComponent.set(componentKey, []);
        }
        let destination = this.fixedAttributesByComponent;
        for (const schemaAttribute of this.schema.attributes) {
            if (schemaAttribute.componentKey === this.componentKey && schemaAttribute.attributeKey === this.attributeKey) {
                destination = this.variationAttributesByComponentKey;
                continue;
            }
            (_a = destination.get(schemaAttribute.componentKey)) === null || _a === void 0 ? void 0 : _a.push(schemaAttribute.attributeKey);
        }
        this.componentKeys = componentKeys;
    }
    getValuesRec(i, current, pickedComponentVariants, values, validationOptions) {
        this.checkTimeout();
        if (i == this.componentKeys.length) {
            this.explain.totalTraversals++;
            const variant = pickedComponentVariants.get(this.componentKey);
            if (!variant || pickedComponentVariants.size !== this.componentKeys.length) {
                throw new Error('Impossible situation');
            }
            const attributeValue = variant[this.attributeKey];
            values.add(attributeValue);
            return;
        }
        const cmpKey = this.componentKeys[i];
        const fixedComponents = new Set(pickedComponentVariants.keys()).add(cmpKey);
        const activatedRules = validationOptions.ignoreAllRules
            ? []
            : this.rules.filter((rule) => rule.deps.componentKeys.every((componentKey) => fixedComponents.has(componentKey)));
        const variants = this.filteredVariantsByComponentKey.get(cmpKey);
        if (!variants) {
            throw new Error(`Cannot find variants for ${cmpKey}`);
        }
        for (const variant of variants) {
            if (this.optimizations.deadBranchElimination) {
                if (cmpKey === this.componentKey && values.has(variant[this.attributeKey])) {
                    this.explain.deadBranchEliminations++;
                    continue;
                }
            }
            const newCurrent = Object.assign(Object.assign({}, current), { [cmpKey]: variant });
            if (activatedRules.every((rule) => rule.fn(newCurrent))) {
                if (this.optimizations.deadBranchElimination) {
                    const mainComponentVariant = pickedComponentVariants.get(this.componentKey);
                    if (mainComponentVariant && values.has(mainComponentVariant[this.attributeKey])) {
                        this.explain.deadBranchEliminations++;
                        break;
                    }
                }
                const previousValuesCount = values.size;
                pickedComponentVariants.set(cmpKey, variant);
                this.explain.perComponentCalls[cmpKey]++;
                this.getValuesRec(i + 1, newCurrent, pickedComponentVariants, values, validationOptions);
                pickedComponentVariants.delete(cmpKey);
                if (this.optimizations.earlyExit) {
                    if (pickedComponentVariants.has(this.componentKey) && values.size > previousValuesCount) {
                        this.explain.earlyExitBranches++;
                        break;
                    }
                }
            }
        }
    }
    setValue(product, componentKey, attributeKey, attributeValue, validationOptions) {
        return this._setValue(product, componentKey, attributeKey, attributeValue, validationOptions);
    }
    _setValue(product, componentKey, attributeKey, attributeValue, validationOptions) {
        this.resetExplain();
        this.explain.operation = 'setValue';
        this.initRecursion(product, componentKey, attributeKey);
        this.buildCombinationsInfo();
        this.logEnabledOptimizations();
        const current = Object.assign(Object.assign({}, product), { [componentKey]: Object.assign(Object.assign({}, product[componentKey]), { [attributeKey]: attributeValue }) });
        this.product = current;
        const result = [];
        this.startedAt = Date.now();
        this.buildFilteredVariants(true);
        this.setValueRec(0, current, new Map(), result, validationOptions);
        this.explain.totalTime = Date.now() - this.startedAt;
        return result[0];
    }
    setValueRec(i, current, pickedComponentVariants, values, validationOptions) {
        this.checkTimeout();
        if (i == this.componentKeys.length) {
            this.explain.totalTraversals++;
            values.push(current);
            return;
        }
        const cmpKey = this.componentKeys[i];
        const fixedComponents = new Set(pickedComponentVariants.keys()).add(cmpKey);
        const activatedRules = validationOptions.ignoreAllRules
            ? []
            : this.rules.filter((rule) => rule.deps.componentKeys.every((componentKey) => fixedComponents.has(componentKey)));
        const variants = this.filteredVariantsByComponentKey.get(cmpKey);
        if (!variants) {
            throw new Error(`Cannot find variants for ${cmpKey}`);
        }
        this.orderVariants(variants, cmpKey);
        for (const variant of variants) {
            const newCurrent = Object.assign(Object.assign({}, current), { [cmpKey]: variant });
            if (activatedRules.every((rule) => rule.fn(newCurrent))) {
                pickedComponentVariants.set(cmpKey, variant);
                this.setValueRec(i + 1, newCurrent, pickedComponentVariants, values, validationOptions);
                pickedComponentVariants.delete(cmpKey);
            }
        }
    }
    checkTimeout() {
        const executionTime = Date.now() - this.startedAt;
        if (executionTime > this.timeout) {
            throw new Error(`Execution time ${executionTime} exceeded timeout ${this.timeout}`);
        }
    }
    orderVariants(variants, cmpKey) {
        const attributes = this.variationAttributesByComponentKey.get(cmpKey);
        if (!attributes) {
            console.warn(`Empty attributes for component ${cmpKey}`);
            return;
        }
        order(variants, this.product[cmpKey], attributes);
    }
    resetExplain() {
        this.explain = Object.assign(Object.assign({}, EMPTY_EXPLAIN), { variantsByComponents: {}, perComponentCalls: {} });
    }
    buildClusters(componentKeys) {
        const ruleComponents = this.rules.map((rule) => rule.deps.componentKeys);
        componentKeys.forEach((componentKey) => ruleComponents.push([componentKey]));
        this.clusters = buildClusters(ruleComponents);
    }
    buildClustersInfo() {
        this.explain.clusters = this.clusters.map((cluster) => Array.from(cluster).sort());
    }
    findClusterComponents(componentKey) {
        const componentCluster = this.clusters.find((cluster) => cluster.has(componentKey));
        if (!componentCluster) {
            throw new Error(`Cannot find component "${componentKey}"`);
        }
        return Array.from(componentCluster);
    }
    buildPerComponentCalls() {
        this.componentKeys.forEach((componentKey) => (this.explain.perComponentCalls[componentKey] = 0));
    }
    buildFilteredVariants(includeMain) {
        this.filteredVariantsByComponentKey.clear();
        for (const [key, value] of this.variantsByComponentKey) {
            this.filteredVariantsByComponentKey.set(key, [...value]);
        }
        let exit = false;
        for (const { componentKey, attributeKey } of this.schema.attributes) {
            if (exit) {
                break;
            }
            if (componentKey === this.componentKey && attributeKey === this.attributeKey) {
                exit = true;
                if (!includeMain) {
                    break;
                }
            }
            const variants = this.filteredVariantsByComponentKey.get(componentKey);
            if (!variants) {
                throw new Error(`Cannot find variants for component "${componentKey}"`);
            }
            const filteredVariants = variants.filter((variant) => variant[attributeKey] === this.product[componentKey][attributeKey]);
            this.filteredVariantsByComponentKey.set(componentKey, filteredVariants);
        }
    }
}

export { LogicEngine };
//# sourceMappingURL=bundle.mjs.map
